/**
 * Authentication Service
 * Orchestrates the ways into a session (each from its URL), leaving one, and
 * reconnecting after an unexpected drop.
 *
 * Every way in joins through one path (`join`): the connection reset, the four
 * steps said as each starts (the auth domain's connectionStep), and its end —
 * joined, or refused. A refusal is kept by its code (domains/join-refusals.ts)
 * for the way in to say, never thrown: a way in reads how its join stands from
 * the auth domain's store. The service writes no store: it says what happens
 * through the auth domain's handlers (domains/auth.ts).
 */

import type { Match } from '@heroiclabs/nakama-js';

import {
  handleJoined,
  handleJoinRefused,
  handleJoinStarted,
  handleJoinStep,
  handleLeftMatch,
  handleModeratorJoined,
  handleSignedIn,
  useAuthStore,
  type ConnectionStep,
} from '../../domains/auth';
import { nakamaService } from '../nakama/nakama-service';
import { resetAllGameStores } from '../connection-reset';
import { readJoinRefusal } from '../../domains/join-refusals';
import { getMatchWorld, type NakamaMatch } from '../../domains/match';
import { useMatchPhaseStore } from '../../domains/match-phase';
import { testPlayerId } from '../../domains/entry-links';
import { GAME_NAME, resolveTeamCode } from '../../domains/sessions';
import { handleEndedSessionLeft } from '../../domains/session-ended';

/** What the portal's moderator link names: the moderator, their team, the world and the match. */
interface ModeratorLink {
  userNakamaCustomId: string;
  cohortId: string;
  worldId: string;
  matchId: string;
  /** The moderator's name, when the link gives one. */
  displayName?: string;
}

/** How a join is sent. */
interface JoinOptions {
  /**
   * Sent by the way in itself while it waits for the session to start (R1):
   * the wait goes on rather than starting afresh, as a player's press does.
   */
  waiting?: boolean;
}

/**
 * Authentication Service
 * Handles the complete authentication flow
 */
class AuthService {
  /**
   * The menu's Disconnect: the player leaves the match, and the entry screen
   * mounts saying so — a way in that joins by itself waits on Rejoin rather
   * than joining again at once (S1). In the end lobby the session is over, so
   * it leaves for the entry's "session ended" state, as the end lobby's Exit
   * does.
   */
  async disconnect(): Promise<void> {
    if (useMatchPhaseStore.getState().phase === 'end_lobby') return this.leaveEndedSession();
    this.leave();
    handleLeftMatch();
  }

  /** Leave the match: the connection and every game store reset, and the entry screen mounts. */
  private leave(): void {
    console.log('[AuthService] Leaving the match...');
    nakamaService.reset();
    resetAllGameStores();
    console.log('[AuthService] Left the match');
  }

  /**
   * Leave a session that is over — the session's-end notice's Exit, or Exit
   * or Disconnect in the end lobby — for the entry screen in its "session
   * ended" state. The state is noted first, so the entry screen's first render
   * already says the session ended and starts no join of its own — a
   * student's link would otherwise join the ended session again by itself, and
   * be refused. In the end lobby the server has not named the world yet: the
   * match's label names it, as the lobby's board reads it (getMatchWorld).
   * Then it leaves the match.
   */
  async leaveEndedSession(): Promise<void> {
    handleEndedSessionLeft(getMatchWorld());
    this.leave();
  }

  /**
   * Leave after a moderator's removal (the removal notice's OK): reload the
   * page, which comes back on the way in its URL names.
   */
  leaveAfterRemoval(): void {
    window.location.reload();
  }

  /**
   * Handle unexpected disconnection (connection lost): every store resets and
   * the entry screen mounts on the way in its URL names, which joins again.
   */
  async handleUnexpectedDisconnect(): Promise<void> {
    console.log('[AuthService] Handling unexpected disconnect...');

    // Reset Nakama service
    nakamaService.reset();

    // Reset all game stores (domains, auth)
    resetAllGameStores();
  }

  /**
   * One join, from its first step to the match. The connection is reset and
   * the steps are said as each starts; joined, the match is the store's and the
   * game screen mounts. Refused, the refusal is kept by its code for the way in
   * to say, and a session not started yet starts a wait — the way in asks
   * again by itself until NOT_STARTED_WAIT runs out — or keeps the one it is
   * in. A join already under way is not started twice.
   */
  private async join(
    what: string,
    enforceFullscreen: boolean,
    steps: (step: (next: ConnectionStep) => void) => Promise<Match>,
    { waiting = false }: JoinOptions = {},
  ): Promise<void> {
    if (useAuthStore.getState().isConnecting) {
      console.log('[AuthService] Already connecting, ignoring duplicate call');
      return;
    }

    nakamaService.reset();
    handleJoinStarted({ enforceFullscreen, waiting });
    console.log(`[AuthService] ${what} — authenticating`);

    try {
      const match = await steps((next) => {
        console.log(`[AuthService] ${what} — ${next}`);
        handleJoinStep(next);
      });
      handleJoined(match as NakamaMatch);
      console.log(`[AuthService] ${what} — joined match ${match.match_id}`);
    } catch (error) {
      console.warn(`[AuthService] ${what} — refused:`, error);
      nakamaService.reset();
      handleJoinRefused(await readJoinRefusal(error));
    }
  }

  /**
   * Connect as a moderator, from the portal's moderator link: to the existing
   * match its id names — never a new one — with no match full screen asked
   * for.
   */
  async connectAsModerator({ userNakamaCustomId, cohortId, worldId, matchId, displayName }: ModeratorLink): Promise<void> {
    await this.join('Moderator join', false, async (step) => {
      const session = await nakamaService.authenticateCustom(userNakamaCustomId, GAME_NAME, worldId, 'moderator');
      handleSignedIn(session);
      step('updatingAccount');
      await nakamaService.updatePlayerAccountAfterAuthenticate(GAME_NAME, worldId, 'moderator', cohortId, displayName);
      step('connectingSocket');
      await nakamaService.connect(session);
      step('joiningMatch');
      const match = await nakamaService.joinMatch(matchId);
      handleModeratorJoined();
      return match;
    });
  }

  /**
   * Connect from a session link (student auto-login via generated URL)
   *
   * Called after the caller has already validated the session timing window.
   * Authenticates the test player the link's class and name make (testPlayerId),
   * and joins the match.
   *
   * @param worldName    - World name to join (e.g. "World_1")
   * @param displayName  - The name the link gives the player
   * @param sessionId    - The session external ID from the invite link.
   *                       Passed to the server so it ONLY finds the scheduled match —
   *                       the player can never accidentally create a new match.
   * @param cohortId     - The class the link names: the server takes no student without one
   */
  async connectFromSessionLink(
    worldName: string,
    displayName: string,
    sessionId: string,
    cohortId: string,
    options?: JoinOptions,
  ): Promise<void> {
    await this.join('Session link', true, async (step) => {
      const session = await nakamaService.authenticateCustom(testPlayerId(cohortId, displayName), GAME_NAME, worldName);
      handleSignedIn(session);
      step('updatingAccount');
      await nakamaService.updatePlayerAccountAfterAuthenticate(GAME_NAME, worldName, undefined, cohortId, displayName);
      step('connectingSocket');
      await nakamaService.connect(session);
      const matchInfo = await nakamaService.createGameMatch(GAME_NAME, worldName, { sessionId });
      step('joiningMatch');
      return nakamaService.joinMatch(matchInfo.match_id);
    }, options);
  }

  /**
   * Connect via the test-session login flow (team code + display name).
   *
   * The caller only provides the numeric team code and a display name.
   * All session/world/cohort details are resolved server-side from the code —
   * identical server-authoritative pattern as the magic link flow.
   */
  async connectFromTestSession(teamCode: string, displayName: string): Promise<void> {
    await this.join('Test session', true, async (step) => {
      // The code's team, world and session, resolved within the first step.
      const resolved = await resolveTeamCode(teamCode);

      // The test player the resolved class and the name make (testPlayerId),
      // as the admin launch and a session link make theirs. No client-side
      // time check here: the server enforces join windows via createGameMatch
      // and joinMatch, as for the magic link.
      const name = displayName.trim();
      const session = await nakamaService.authenticateCustom(testPlayerId(resolved.cohortId, name), GAME_NAME, resolved.worldName);
      handleSignedIn(session);
      step('updatingAccount');
      await nakamaService.updatePlayerAccountAfterAuthenticate(GAME_NAME, resolved.worldName, undefined, resolved.cohortId, name);
      step('connectingSocket');
      await nakamaService.connect(session);
      const matchInfo = await nakamaService.createGameMatch(GAME_NAME, resolved.worldName, { sessionId: resolved.sessionExternalId });
      step('joiningMatch');
      return nakamaService.joinMatch(matchInfo.match_id);
    });
  }

  /**
   * Connect as a real student to a scheduled session. Unlike admin-launch, this
   * never creates a match — it resolves the existing Nakama match by sessionId
   * (same mechanism as session-link / test-session). Unlike session-link, it
   * authenticates with the student's real nakamaCustomId so the player record
   * is correctly linked to their account.
   */
  async connectFromStudentJoin(
    nakamaCustomId: string,
    worldName: string,
    cohortId: string,
    sessionExternalId: string,
    displayName: string,
    options?: JoinOptions,
  ): Promise<void> {
    await this.join('Student join', true, async (step) => {
      const session = await nakamaService.authenticateCustom(nakamaCustomId, GAME_NAME, worldName);
      handleSignedIn(session);
      step('updatingAccount');
      await nakamaService.updatePlayerAccountAfterAuthenticate(GAME_NAME, worldName, undefined, cohortId, displayName);
      step('connectingSocket');
      await nakamaService.connect(session);
      const matchInfo = await nakamaService.createGameMatch(GAME_NAME, worldName, { sessionId: sessionExternalId });
      step('joiningMatch');
      return nakamaService.joinMatch(matchInfo.match_id);
    }, options);
  }

  /**
   * Connect via an admin-launch URL.
   * The portal-backend has already ensured a match exists; this method authenticates
   * the test player the class and the name make (testPlayerId) and joins the
   * pre-existing match directly — never creating a new one.
   *
   * @param worldName   World name (e.g. "tas")
   * @param matchId     Match ID returned by the backend
   * @param cohortId    Cohort ID for the player
   * @param displayName Player display name
   */
  async connectFromAdminLaunch(
    worldName: string,
    matchId: string,
    cohortId: string,
    displayName: string,
  ): Promise<void> {
    await this.join('Admin launch', true, async (step) => {
      const session = await nakamaService.authenticateCustom(testPlayerId(cohortId, displayName), GAME_NAME, worldName);
      handleSignedIn(session);
      step('updatingAccount');
      await nakamaService.updatePlayerAccountAfterAuthenticate(GAME_NAME, worldName, undefined, cohortId, displayName);
      step('connectingSocket');
      await nakamaService.connect(session);
      step('joiningMatch');
      return nakamaService.joinMatch(matchId);
    });
  }
}

// Export singleton instance
export const authService = new AuthService();
